import { computed, Ref, useContext } from '@nuxtjs/composition-api';
import type { Route } from 'vue-router';

import { StockItem, useImage } from '~/composables';
import { useWishlist } from '~/modules/wishlist/composables/useWishlist';
import { useProduct } from '~/modules/catalog/product/composables/useProduct';
import type { ProductAvailable } from '~/modules/catalog/product/helpers/stockHelpers';
import { Aggregation } from '@vue-storefront/magento-types';
import {
  getName,
  getPrice,
  getProductThumbnailImage,
  isProductBestseller,
  isSpecialPrice,
  getIsNewProduct,
  isProductConfigurable,
  getAttributeLabel,
  getConcatenatedVariantsLabels,
  getAvailableType,
  getCheapestVariant,
  getQtyConfig,
  isProductRecommended,
} from '~/modules/catalog/product/getters/productGetters';
import { useAddToCart } from '~/helpers/cart/addToCart';
import type { Product } from '~/modules/catalog/product/types';
import useOmnibusPrice from '~/modules/catalog/product/composables/useOmnibusPrice';

export interface ProductCommonCardProps {
  isBestseller: boolean;
  isNew: boolean;
  isProductConfigurable: boolean;

  title: string;
  variants: string;
  available: ProductAvailable;
  image: string;
  isAddedToCart: boolean;
  link: Route;
  manufacturer: string;
  qtyConfig: StockItem;

  isInWishlist: boolean;

  regularPrice: string;
  specialPrice: string;
  omnibusPrice: string;

  finalPriceRaw: number;
}

export type ProductWithCommonProductCardProps = Product & { commonProps: ProductCommonCardProps };

export const gettersForProductWithExtendedData = {
  getPrices(extendedProduct: ProductWithCommonProductCardProps) {
    // @ts-ignore
    return extendedProduct.extendedProductData?.prices;
  },
  getAvailableStatus(extendedProduct: ProductWithCommonProductCardProps) {
    // @ts-ignore
    return extendedProduct.extendedProductData?.available.status;
  },
  getFinalPrice(extendedProduct: ProductWithCommonProductCardProps) {
    return this.getPrices(extendedProduct).finalPrice;
  },
  getFinalPriceRaw(extendedProduct: ProductWithCommonProductCardProps) {
    return this.getPrices(extendedProduct).finalPriceRaw;
  },
  getOmnibusPrice(extendedProduct: ProductWithCommonProductCardProps) {
    return this.getPrices(extendedProduct).omnibusPrice;
  },
  getFinalNettoPrice(extendedProduct: ProductWithCommonProductCardProps) {
    return this.getPrices(extendedProduct).finalNettoPrice;
  },
};

export const useProductsWithCommonProductCardProps = ({
  products,
  aggregations,
}: {
  products: Ref<Product[]>;
  aggregations?: Ref<Aggregation[]>;
}) => {
  const { getMagentoImage } = useImage();
  const { isInWishlist } = useWishlist();
  const { isInCart } = useAddToCart();
  const { getProductPath } = useProduct();
  const context = useContext();
  const { omnibusPrices } = useOmnibusPrice();
  /**
   * Most props of SfProductCard and SfProductCardHorizontal are the same.
   * To avoid passing tens of props to both components two times,
   * instead the below object is passed to them using `v-bind="product.commonProps"`
   */

  /**
   * TODO: change getIsNewProduct name,
   * getAttributeLabel move to helpers,
   */
  const productsWithCommonProductCardProps = computed<ProductWithCommonProductCardProps[]>(() => products.value.map((product, index) => {
    const aggregationsValue = aggregations?.value;
    const { sku: parentSku, id: parentId } = product;
    const { sku: currentProductSku, id: currentProductId } = isProductConfigurable(product) && product.variants
      ? getCheapestVariant(product)
      : product;

    const extendedProps: Record<string, any> = {};

    /**
       * Prepare omnibus price for product
       * @param prod
       */
    const prepareOmnibusPrice = (prod: Product) => {
      const { sku } = prod;
      const price = getPrice(prod);
      const regularPriceRaw = price.regular;
      const finalPriceRaw = price.final;
      const finalNettoPriceRaw = price.finalNetto;
      const omnibusPriceValue = omnibusPrices.value ? omnibusPrices.value[sku] : null;
      const specialPriceRaw = isSpecialPrice(getPrice(prod).special, omnibusPriceValue) ? getPrice(prod).special : '';
      const omnibusPriceRaw = isSpecialPrice(getPrice(prod).special, omnibusPriceValue) ? omnibusPriceValue : '';
      return {
        regularPrice: context.$fc(regularPriceRaw),
        finalPrice: context.$fc(finalPriceRaw),
        finalNettoPrice: context.$fc(finalNettoPriceRaw as number),
        specialPrice: specialPriceRaw ? context.$fc(specialPriceRaw) : '',
        omnibusPrice: omnibusPriceRaw ? context.$fc(omnibusPriceRaw) : '',
        finalPriceRaw,
      };
    };

    if (isProductConfigurable(product)) {
      const { short_description: parentShortDescription, configurable_options: configurableOptions, variants: configurableVariants } = product;

      if (configurableVariants) {
        extendedProps.configurableVariants = configurableVariants.map((variant) => ({
          ...variant,
          product: {
            ...variant.product,
            extendedProductData: {
              prices: prepareOmnibusPrice(variant.product as Product),
              available: getAvailableType(variant.product as Product),
            },
          },
        }));
      }
      if (configurableOptions) extendedProps.configurableOptions = configurableOptions;
      if (parentShortDescription) extendedProps.parentShortDescription = parentShortDescription?.html || '';
    }

    const badgeProps = {
      isBestseller: isProductBestseller(product),
      isRecommended: isProductRecommended(product),
      isNew: getIsNewProduct(product),
      isProductConfigurable: isProductConfigurable(product),
    };

    const wishlistProps = {
      isInWishlist: isInWishlist({ product }),
    };

    const priceProps = prepareOmnibusPrice(product);
    const link = context.localeRoute(getProductPath(product));
    const manufacturer = getAttributeLabel('manufacturer', product.manufacturer, aggregationsValue);
    const qtyConfig = getQtyConfig(product);
    const categories = product?.categories || null;

    const commonProps = {
      parentSku,
      sku: currentProductSku,
      parentId,
      id: currentProductId,
      title: getName(product),
      variants: context.i18n.t(getConcatenatedVariantsLabels(product) as string) as string,
      available: getAvailableType(product),
      image: getMagentoImage(getProductThumbnailImage(product)),
      isAddedToCart: isInCart(product),
      link,
      manufacturer,
      qtyConfig,
      style: { '--index': index, order: index * 10 },
      mainCategory: product?.main_category,
      categories,
      ...badgeProps,
      ...wishlistProps,
      ...priceProps,
      ...extendedProps,
    };

    return {
      ...product,
      commonProps,
    };
  }));
  return { productsWithCommonProductCardProps };
};
